import { QueryClient } from '@tanstack/react-query';
import { LabelsUpdates } from '@labels/types';
import { LabelAssignableJSON } from '@labels/models/labelAssignable';
import type { LabelJSON } from '@labels/models/label';
import { applyLabelEdit, applyLabelRemoval, updateItemLabels } from '@labels/utils';
import ProjectMember from '@projectMembers/models/projectMember';
import { QueryFilters, QueryKeys } from './keysAndFilters';
import { QueryDataUpdater, updateQueriesData, updateQueryData } from '../../../queries/updaters';
import { QueryType } from '../../../queries/types';

import { queryClient as globalQueryClient } from '../../../../store/store';

import { AssetRoundJSON } from '../../projectView/models/assetRound';
import { AssetJSON } from '../../projectView/models/asset';
import { updateItemAssignees } from '../../projectView/utils';

export class AssetDetailQueryDataUpdater {
	static forEditedLabel = (
		labelId: number,
		{ name, base_color, hover_color }: Pick<LabelJSON, 'name' | 'base_color' | 'hover_color'>,
		options?: { queryClient?: QueryClient }
	) => {
		this.updateLabelsOnAllAssetDetails(
			labels => applyLabelEdit( labels, { id: labelId, name, base_color, hover_color } ),
			options
		);
	}

	static forDeletedLabel = (
		labelId: number,
		options?: { queryClient?: QueryClient }
	) => {
		this.updateLabelsOnAllAssetDetails(
			labels => applyLabelRemoval( labels, labelId ),
			options
		);
	}

	private static updateLabelsOnAllAssetDetails = (
		transform: ( labels: LabelJSON[] ) => LabelJSON[],
		{ queryClient = globalQueryClient }: { queryClient?: QueryClient } = {}
	) => {
		updateQueriesData(
			queryClient,
			QueryFilters.forAssetDetail(),
			( asset: AssetJSON ) => ( {
				...asset,
				labels: asset.labels ? transform( asset.labels ) : asset.labels
			} )
		);
	}

	static forLabelsUpdate = (
		assetsIds: number[],
		updates: LabelsUpdates,
		orderedLabelsIDs?: number[],
		{ queryClient = globalQueryClient }: { queryClient?: QueryClient } = {}
	) => {
		assetsIds.forEach( ( assetID ) => {
			updateQueryData(
				queryClient,
				QueryKeys.forAssetDetail( assetID ),
				( asset: LabelAssignableJSON ) => updateItemLabels( {
					item: asset, updates, orderedLabelsIDs
				} )
			);
		} );
	}

	static forCreatedAssetRound = (
		assetID: number,
		newRoundJson: AssetRoundJSON,
		{ queryClient = globalQueryClient }: { queryClient?: QueryClient } = {}
	) => {
		const lastActivityAt = Date();

		this.updateAssetDetail(
			assetID,
			asset => ( {
				...asset,
				name: newRoundJson.name,
				current_version: newRoundJson,
				version_id: newRoundJson.id,
				asset_versions_count: asset.asset_versions_count + 1,
				last_activity_at: lastActivityAt,
				updated_at: lastActivityAt,
				has_comments_in_current_round: false
			} ),
			{ queryClient }
		);
	}

	static forUpdatedCurrentRound = (
		assetID: number,
		currentRoundJson: AssetRoundJSON,
		unpreviewable?: boolean,
		{ metadataRating, queryClient = globalQueryClient }: {
			metadataRating?: number | null,
			queryClient?: QueryClient
		} = {}
	) => {
		this.updateAssetDetail(
			assetID,
			asset => ( {
				...asset,
				name: currentRoundJson.name,
				current_version: currentRoundJson,
				...( unpreviewable !== undefined && { unpreviewable } ),
				...( metadataRating !== undefined && { metadata_rating: metadataRating } )
			} ),
			{ queryClient }
		);
	}

	static forApprovalStatusChange = (
		assetID: number,
		approvedAt: string | null,
		{ queryClient = globalQueryClient }: { queryClient?: QueryClient } = {}
	) => {
		this.updateAssetDetail(
			assetID,
			asset => ( {
				...asset,
				approved_at: approvedAt
			} ),
			{ queryClient }
		);
	}

	static forFetchedRounds = (
		rounds: AssetRoundJSON[],
		{ queryClient = globalQueryClient }: { queryClient?: QueryClient } = {}
	) => {
		rounds.forEach( ( round ) => {
			this.updateAssetDetail(
				round.asset_id,
				asset => (
					asset.version_id === round.id ? ( {
						...asset,
						current_version: round
					} ) : asset
				),
				{ queryClient }
			);
		} );
	}

	static forAssigneesUpdate = (
		assetID: number,
		usersToAddIDs: number[],
		usersToRemoveIDs: number[],
		projectMembers: ProjectMember[],
		{ queryClient = globalQueryClient }: { queryClient?: QueryClient } = {}
	) => {
		this.updateAssetDetail(
			assetID,
			asset => updateItemAssignees( {
				item: asset,
				usersToAddIDs,
				usersToRemoveIDs,
				projectMembers
			} ),
			{ queryClient }
		);
	}

	private static updateAssetDetail(
		assetID: number,
		updater: QueryDataUpdater<AssetJSON>,
		{ queryClient = globalQueryClient, queryType = 'all' }: { queryClient?: QueryClient, queryType?: QueryType } = {}
	) {
		updateQueriesData(
			queryClient,
			QueryFilters.forAssetDetail( assetID, { queryType } ),
			updater
		);
	}
}
